import { cn } from '@shared/lib';
import { brandRgba, brandShadowRgba } from '@shared/constants';

type AiMoodBackdropProps = {
  /** `full` — landing hero intensity; `subtle` — admin inner pages */
  variant?: 'full' | 'subtle';
  className?: string;
};

/**
 * Iridescent AI mood backdrop (orbs, aurora, grid, particles).
 * `subtle` is tuned for Persona Library — visible but not distracting.
 */
export function AiMoodBackdrop({ variant = 'full', className }: AiMoodBackdropProps) {
  const isSubtle = variant === 'subtle';

  const orbOpacity = isSubtle ? 0.42 : 1;
  const washOpacity = isSubtle ? 0.55 : 1;
  const meshOpacity = isSubtle ? 0.1 : 0.22;
  const auroraOpacity = isSubtle ? 0.2 : 0.38;
  const particleOpacity = isSubtle ? 0.28 : 0.55;
  const gridOpacity = isSubtle ? 0.028 : 0.045;

  return (
    <div
      aria-hidden
      className={cn('pointer-events-none absolute inset-0 overflow-hidden', className)}
    >
      {/* Ambient wash */}
      <div
        className="absolute inset-0"
        style={{
          opacity: washOpacity,
          background: [
            `radial-gradient(120% 80% at 82% 8%, ${brandRgba('accentLight', 0.22)} 0%, transparent 50%)`,
            `radial-gradient(100% 70% at 70% 45%, ${brandRgba('accent', 0.16)} 0%, transparent 55%)`,
            `radial-gradient(90% 60% at 30% 20%, ${brandRgba('accentMid', 0.08)} 0%, transparent 50%)`,
          ].join(', '),
        }}
      />

      {/* Sky orb — top right */}
      <div
        className={cn(
          'absolute -right-24 -top-16 rounded-full blur-[120px] ai-orb-breathe',
          isSubtle ? 'h-[520px] w-[520px]' : 'h-[820px] w-[820px] -right-32 -top-24 blur-[140px]'
        )}
        style={{
          opacity: orbOpacity * 0.9,
          background:
            `radial-gradient(closest-side, ${brandRgba('accentLight', 0.48)}, ${brandRgba('accent', 0.22)} 45%, transparent 78%)`,
        }}
      />

      {/* Violet orb — carousel focal point */}
      <div
        className={cn(
          'absolute left-[18%] top-[32%] rounded-full blur-[130px] ai-orb-breathe-slow',
          isSubtle ? 'h-[420px] w-[420px]' : 'h-[520px] w-[520px] -left-24 -top-20'
        )}
        style={{
          opacity: orbOpacity * 0.65,
          animationDelay: '1.2s',
          background:
            `radial-gradient(closest-side, ${brandRgba('accentMid', 0.32)}, ${brandRgba('accent', 0.12)} 50%, transparent 75%)`,
        }}
      />

      {/* Magenta orb — bottom right */}
      <div
        className={cn(
          'absolute -bottom-20 -right-12 rounded-full blur-[120px]',
          isSubtle ? 'h-[380px] w-[380px]' : 'h-[680px] w-[680px] -bottom-32 -right-20 blur-[140px]'
        )}
        style={{
          opacity: orbOpacity * 0.55,
          background:
            `radial-gradient(closest-side, ${brandRgba('accentHighlight', 0.28)}, ${brandRgba('accentDeep', 0.14)} 50%, transparent 75%)`,
        }}
      />

      {/* Pearl center glow */}
      <div
        className="absolute left-1/2 top-[42%] h-[320px] w-[320px] -translate-x-1/2 rounded-full blur-[150px]"
        style={{
          opacity: isSubtle ? 0.35 : 0.45,
          background:
            `radial-gradient(closest-side, ${brandRgba('accentTint', 0.5)}, ${brandRgba('accentTint', 0.28)} 50%, transparent 70%)`,
        }}
      />

      {/* Conic holographic mesh */}
      <div
        className="absolute -inset-[15%] blur-[90px]"
        style={{
          opacity: meshOpacity,
          background:
            `conic-gradient(from 200deg at 58% 42%, ${brandRgba('accentLight', 0.45)}, ${brandRgba('accent', 0.35)}, ${brandRgba('accentMid', 0.4)}, ${brandRgba('accentDeep', 0.32)}, ${brandRgba('accentHighlight', 0.28)}, ${brandRgba('accentLight', 0.45)})`,
        }}
      />

      {/* Aurora ribbon */}
      <div
        className="absolute inset-x-0 top-[38%] h-[280px] blur-[72px]"
        style={{
          opacity: auroraOpacity,
          background:
            `linear-gradient(95deg, transparent 0%, ${brandRgba('accentLight', 0.28)} 22%, ${brandRgba('accentMid', 0.38)} 50%, ${brandRgba('accentDeep', 0.28)} 78%, transparent 100%)`,
        }}
      />

      {/* Iridescent scan band */}
      <div
        className="absolute inset-x-0 top-[48%] h-px"
        style={{
          opacity: isSubtle ? 0.45 : 0.6,
          background:
            `linear-gradient(90deg, transparent 0%, ${brandRgba('accent', 0.45)} 35%, ${brandRgba('accentMid', 0.65)} 50%, ${brandRgba('accentDeep', 0.45)} 65%, transparent 100%)`,
          boxShadow: isSubtle
            ? `0 0 48px 12px ${brandShadowRgba('accent', 0.12)}, 0 0 72px 24px ${brandShadowRgba('accentLight', 0.08)}`
            : `0 0 80px 20px ${brandShadowRgba('accent', 0.25)}, 0 0 120px 40px ${brandShadowRgba('accentLight', 0.15)}`,
        }}
      />

      {/* AI particles */}
      <div
        className="absolute inset-0"
        style={{
          opacity: particleOpacity,
          backgroundImage: [
            `radial-gradient(circle at 78% 18%, ${brandRgba('accentLight', 0.85)} 0, ${brandRgba('accentLight', 0.85)} 1.5px, transparent 2px)`,
            `radial-gradient(circle at 88% 42%, ${brandRgba('accent', 0.75)} 0, ${brandRgba('accent', 0.75)} 1px, transparent 1.5px)`,
            `radial-gradient(circle at 72% 65%, ${brandRgba('accentDeep', 0.65)} 0, ${brandRgba('accentDeep', 0.65)} 1px, transparent 1.5px)`,
            `radial-gradient(circle at 55% 52%, ${brandRgba('accentLight', 0.45)} 0, ${brandRgba('accentLight', 0.45)} 1px, transparent 1.5px)`,
            `radial-gradient(circle at 38% 28%, ${brandRgba('accentMid', 0.55)} 0, ${brandRgba('accentMid', 0.55)} 1px, transparent 1.5px)`,
          ].join(', '),
        }}
      />

      {/* Tech grid */}
      <div
        className="absolute inset-0"
        style={{
          opacity: gridOpacity,
          backgroundImage:
            `linear-gradient(to right, ${brandRgba('accent', 0.55)} 1px, transparent 1px), linear-gradient(to bottom, ${brandRgba('accent', 0.55)} 1px, transparent 1px)`,
          backgroundSize: '56px 56px',
        }}
      />

      {/* Diagonal mesh hint */}
      <div
        className="absolute inset-0"
        style={{
          opacity: isSubtle ? 0.018 : 0.025,
          backgroundImage:
            `repeating-linear-gradient(125deg, transparent, transparent 80px, ${brandRgba('accentLight', 0.35)} 80px, ${brandRgba('accentLight', 0.35)} 81px)`,
        }}
      />
    </div>
  );
}
